import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Contact } from '@mailpilot/shared';
import { api } from '../../lib/api';
import { qk } from '../../lib/keys';
import { isValidEmail } from '../../lib/format';
import { Button, ChipInput, Field, Input, Modal, Switch, Textarea, useToast } from '../ui';

/** Create (contact = null) or edit a contact. */
export function ContactFormModal({ open, onClose, contact, onSaved }: { open: boolean; onClose: () => void; contact: Contact | null; onSaved?: (c: Contact) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState('');
  const [emails, setEmails] = useState<string[]>([]);
  const [company, setCompany] = useState('');
  const [notes, setNotes] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [favorite, setFavorite] = useState(false);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(contact?.name ?? '');
    setEmails(contact?.emails ?? []);
    setCompany(contact?.company ?? '');
    setNotes(contact?.notes ?? '');
    setTags(contact?.tags ?? []);
    setFavorite(contact?.favorite ?? false);
    setTouched(false);
  }, [open, contact]);

  const badEmails = emails.filter((e) => !isValidEmail(e));
  const emailError = badEmails.length ? `Not a valid email: ${badEmails.join(', ')}` : touched && !emails.length ? 'Add at least one email address.' : undefined;

  const save = useMutation({
    mutationFn: async () => {
      const body = { name: name.trim(), emails: emails.map((e) => e.toLowerCase()), company: company.trim() || null, notes, tags, favorite };
      if (contact) return (await api('PATCH /api/contacts/:id', { params: { id: contact.id }, body })).contact;
      return (await api('POST /api/contacts', { body: { ...body, source: 'manual' } })).contact;
    },
    onSuccess: (c) => {
      toast.success(contact ? 'Contact updated' : 'Contact saved');
      void qc.invalidateQueries({ queryKey: qk.contactsRoot });
      void qc.invalidateQueries({ queryKey: qk.contact(c.id) });
      void qc.invalidateQueries({ queryKey: qk.contactSuggestions });
      void qc.invalidateQueries({ queryKey: qk.contactDuplicates });
      onSaved?.(c);
      onClose();
    },
    onError: (e) => toast.error(e),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!emails.length || badEmails.length) return;
    save.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={contact ? 'Edit contact' : 'New contact'}
      description="Only what you enter is saved — MailPilot never invents contact details."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="contact-form" loading={save.isPending}>
            {contact ? 'Save changes' : 'Save contact'}
          </Button>
        </>
      }
    >
      <form id="contact-form" onSubmit={submit} className="space-y-4" noValidate>
        <Field label="Name">
          {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" data-autofocus className="max-md:h-11" />}
        </Field>
        <Field label="Email addresses" required error={emailError} hint="Press Enter or comma after each address.">
          {() => <ChipInput label="Email addresses" value={emails} onChange={setEmails} placeholder="name@example.com" />}
        </Field>
        <Field label="Company">
          {(id) => <Input id={id} value={company} onChange={(e) => setCompany(e.target.value)} className="max-md:h-11" />}
        </Field>
        <Field label="Tags">
          {() => <ChipInput label="Tags" value={tags} onChange={setTags} placeholder="client, supplier…" />}
        </Field>
        <Field label="Notes">
          {(id) => <Textarea id={id} value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} />}
        </Field>
        <Switch checked={favorite} onChange={setFavorite} label="Favourite" description="Favourites are easy to filter and find." />
      </form>
    </Modal>
  );
}
